import React, { FC, memo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { useFeatureFlags } from "@/components/FeatureFlags";
import { Section } from "@/components/Section";
import { UserContext } from "@/components/User";
import { fetchEmailPreferences, IEmailPreferences, putEmailPreferences } from "@/data/email-preferences";
import { FEATURE_FLAG } from "@/data/feature-flags";
import { Flex } from "@/eworkitComponents";
import { useFetcher } from "@/hooks/http";
import { useToasts } from "@/views/components/Toast/useToasts";

import { ApplicationSettings } from "./ApplicationSettings";
import { AssignmentSettings } from "./AssignmentSettings";
import { ChatSettings } from "./ChatSettings";
import { RecommendedJobsSettings } from "./RecommendedJobsSettings";
import { TeamSettings } from "./TeamSettings";

export const EmailNotificationsSettings: FC = memo(() => {
  const User = useContext(UserContext);
  const { addToast } = useToasts();
  const companyId = User.contextCompany?.id;
  const { t } = useTranslation("common");
  const isManager = User.hasCompanyManagerPrivileges();
  const { isFeatureEnabled } = useFeatureFlags();

  const emailPreferencesFetchState = useFetcher(fetchEmailPreferences, { companyId }, [companyId]);

  const updateEmailPreferences = useCallback(
    async (preferences: IEmailPreferences) => {
      try {
        const response = await putEmailPreferences({ companyId }, preferences);
        emailPreferencesFetchState.updatePayload(response);
      } catch (error) {
        addToast({
          text: t("VALIDATION_MESSAGES.GENERAL"),
          type: "warning",
        });
      }
    },
    [emailPreferencesFetchState.updatePayload, companyId, addToast],
  );

  const handleEmailPreferencesChange = useCallback(
    <T extends IEmailPreferences, K extends keyof T>(key: K, value: T[K]) => {
      if (emailPreferencesFetchState.payload) {
        updateEmailPreferences({
          ...emailPreferencesFetchState.payload,
          [key]: value,
        });
      }
    },
    [updateEmailPreferences, emailPreferencesFetchState.payload],
  );

  return (
    <Section padding={["lg"]} borderRadius>
      <Flex direction="column" grow={1} innerMargin="lg">
        {emailPreferencesFetchState.payload && (
          <>
            <RecommendedJobsSettings
              emailPreferences={emailPreferencesFetchState.payload}
              onChange={handleEmailPreferencesChange}
            />
            {(isManager || !User.contextCompany) && (
              <ApplicationSettings
                emailPreferences={emailPreferencesFetchState.payload}
                onChange={handleEmailPreferencesChange}
              />
            )}

            {isManager && (
              <AssignmentSettings
                emailPreferences={emailPreferencesFetchState.payload}
                onChange={handleEmailPreferencesChange}
              />
            )}

            {isManager && (
              <TeamSettings
                emailPreferences={emailPreferencesFetchState.payload}
                onChange={handleEmailPreferencesChange}
              />
            )}

            {isFeatureEnabled(FEATURE_FLAG.MESSAGE_CENTER) && (isManager || !User.contextCompany) && (
              <ChatSettings
                emailPreferences={emailPreferencesFetchState.payload}
                onChange={handleEmailPreferencesChange}
              />
            )}

            {/* Notifications for Time Reports are not part of the release soo for these settings have to be hidden */}
            {/*<TimeReportsSettings*/}
            {/*  emailPreferences={emailPreferencesFetchState.payload}*/}
            {/*  onChange={handleEmailPreferencesChange}*/}
            {/*/>*/}
          </>
        )}
      </Flex>
    </Section>
  );
});
